<template>
  <v-card flat class="script-options-card">
    <div class="script-options-card__head">
      <div class="script-options-card__head-icon">
        <v-icon small color="white">mdi-tune-variant</v-icon>
      </div>
      <div>
        <div class="script-options-card__title">Run options</div>
        <div class="script-options-card__subtitle">How this script is executed on agents</div>
      </div>
    </div>

    <v-row dense class="px-1 pb-1">
      <v-col v-if="showKill" cols="12" md="6">
        <button
          type="button"
          class="script-option-tile"
          :class="{ 'script-option-tile--on': runKillScript }"
          :disabled="disabled"
          @click="toggle('runKillScript')"
        >
          <span class="script-option-tile__icon" :class="{ 'script-option-tile__icon--on': runKillScript }">
            <v-icon small>mdi-stop-circle-outline</v-icon>
          </span>
          <span class="script-option-tile__text">
            <span class="script-option-tile__label">Kill on timeout</span>
            <span class="script-option-tile__hint">Runs kill script when time limit is reached</span>
          </span>
          <v-switch
            :input-value="runKillScript"
            inset
            dense
            hide-details
            color="primary"
            class="script-option-tile__switch"
            :disabled="disabled"
            @click.stop
            @change="$emit('update:runKillScript', $event)"
          />
        </button>
      </v-col>

      <v-col cols="12" md="6">
        <button
          type="button"
          class="script-option-tile"
          :class="{ 'script-option-tile--on': rdp }"
          :disabled="disabled"
          @click="toggle('rdp')"
        >
          <span class="script-option-tile__icon" :class="{ 'script-option-tile__icon--on': rdp }">
            <v-icon small>mdi-remote-desktop</v-icon>
          </span>
          <span class="script-option-tile__text">
            <span class="script-option-tile__label">RDP session</span>
            <span class="script-option-tile__hint">Runs inside a remote desktop session</span>
          </span>
          <v-switch
            :input-value="rdp"
            inset
            dense
            hide-details
            color="primary"
            class="script-option-tile__switch"
            :disabled="disabled"
            @click.stop
            @change="$emit('update:rdp', $event)"
          />
        </button>
      </v-col>

      <v-col cols="12" md="6">
        <button
          type="button"
          class="script-option-tile"
          :class="{ 'script-option-tile--on': queue }"
          :disabled="disabled"
          @click="toggle('queue')"
        >
          <span class="script-option-tile__icon" :class="{ 'script-option-tile__icon--on': queue }">
            <v-icon small>mdi-tray-full</v-icon>
          </span>
          <span class="script-option-tile__text">
            <span class="script-option-tile__label">Queue</span>
            <span class="script-option-tile__hint">Processes items from a work queue</span>
          </span>
          <v-switch
            :input-value="queue"
            inset
            dense
            hide-details
            color="primary"
            class="script-option-tile__switch"
            :disabled="disabled"
            @click.stop
            @change="$emit('update:queue', $event)"
          />
        </button>
      </v-col>

      <v-col cols="12">
        <div
          class="script-option-tile script-option-tile--priority script-option-tile--priority-full"
          :class="{ 'script-option-tile--on': priorityValue > 0 }"
        >
          <span
            class="script-option-tile__icon"
            :class="{ 'script-option-tile__icon--on': priorityValue > 0 }"
          >
            <v-icon small>mdi-flag-outline</v-icon>
          </span>
          <span class="script-option-tile__text script-option-tile__text--priority">
            <span class="script-option-tile__label">Queue priority</span>
            <span class="script-option-tile__hint">Runs first when the agent is busy</span>
          </span>
          <div class="script-priority-radios" role="radiogroup" aria-label="Queue priority">
            <button
              v-for="item in priorityItems"
              :key="item.value"
              type="button"
              role="radio"
              class="script-priority-radio"
              :class="{ 'script-priority-radio--active': priorityValue === item.value }"
              :aria-checked="priorityValue === item.value ? 'true' : 'false'"
              :disabled="disabled"
              @click="setPriority(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>
      </v-col>

      <v-col v-if="showUploadToggle" cols="12" class="script-upload-row-col">
        <v-row dense align="stretch" class="script-upload-row">
          <v-col cols="12" :md="uploadNewFile ? 6 : 12" lg="5">
            <button
              type="button"
              class="script-option-tile script-option-tile--upload"
              :class="{ 'script-option-tile--on': uploadNewFile }"
              :disabled="disabled"
              @click="toggle('uploadNewFile')"
            >
              <span class="script-option-tile__icon" :class="{ 'script-option-tile__icon--on': uploadNewFile }">
                <v-icon small>mdi-zip-box-outline</v-icon>
              </span>
              <span class="script-option-tile__text">
                <span class="script-option-tile__label">Upload new file</span>
                <span class="script-option-tile__hint">Replace the script package (.zip)</span>
              </span>
              <v-switch
                :input-value="uploadNewFile"
                inset
                dense
                hide-details
                color="primary"
                class="script-option-tile__switch"
                :disabled="disabled"
                @click.stop
                @change="$emit('update:uploadNewFile', $event)"
              />
            </button>
          </v-col>
          <v-col v-if="uploadNewFile" cols="12" md="6" lg="7" class="script-upload-slot-col">
            <div class="script-upload-inline">
              <slot name="file-upload" />
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
const EMIT_MAP = {
  rdp: "update:rdp",
  queue: "update:queue",
  runKillScript: "update:runKillScript",
  uploadNewFile: "update:uploadNewFile",
};

const PRIORITY_ITEMS = [
  { label: "Normal", value: 0 },
  { label: "High", value: 1 },
  { label: "Critical", value: 2 },
];

export default {
  name: "ScriptOptionsPanel",
  props: {
    rdp: Boolean,
    queue: Boolean,
    priority: { type: Number, default: 0 },
    runKillScript: Boolean,
    uploadNewFile: Boolean,
    showKill: { type: Boolean, default: false },
    showUploadToggle: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  computed: {
    priorityValue() {
      const value = Number(this.priority);
      return Number.isFinite(value) ? value : 0;
    },
    priorityItems() {
      return PRIORITY_ITEMS;
    },
  },
  methods: {
    toggle(key) {
      if (this.disabled) return;
      const current = this[key];
      this.$emit(EMIT_MAP[key], !current);
    },
    setPriority(value) {
      if (this.disabled) return;
      this.$emit("update:priority", value);
    },
  },
};
</script>

<style scoped>
.script-options-card {
  background: linear-gradient(180deg, #faf7f9 0%, #fff 100%);
  border: 1px solid rgba(102, 63, 92, 0.14);
  border-radius: 12px;
  padding: 14px 14px 10px;
  box-shadow: 0 1px 3px rgba(102, 63, 92, 0.06);
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.script-options-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 0 4px;
}

.script-options-card__head-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7a4d6c 0%, #663f5c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(102, 63, 92, 0.25);
}

.script-options-card__title {
  font-size: 15px;
  font-weight: 700;
  color: #663f5c;
  line-height: 1.2;
}

.script-options-card__subtitle {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 2px;
}

.script-option-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-height: 76px;
  padding: 12px 12px 12px 14px;
  margin: 0;
  text-align: left;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 12px;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    transform 0.15s ease;
}

.script-option-tile:hover:not(:disabled) {
  border-color: rgba(102, 63, 92, 0.35);
  box-shadow: 0 4px 14px rgba(102, 63, 92, 0.1);
  transform: translateY(-1px);
}

.script-option-tile--on {
  border-color: rgba(102, 63, 92, 0.45);
  background: linear-gradient(135deg, #fff 0%, #f8f3f6 100%);
  box-shadow: 0 2px 12px rgba(102, 63, 92, 0.12);
}

.script-option-tile:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.script-option-tile__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0ecee;
  color: rgba(102, 63, 92, 0.55);
  transition: background 0.2s ease, color 0.2s ease;
}

.script-option-tile__icon--on {
  background: linear-gradient(135deg, #7a4d6c 0%, #663f5c 100%);
  color: #fff;
}

/* Vuetify v-icon parent color'ı miras almaz — seçiliyken beyaz */
.script-option-tile__icon--on >>> .v-icon {
  color: #fff !important;
  caret-color: #fff;
}

.script-option-tile__text {
  flex: 1;
  min-width: 0;
  padding-right: 4px;
}

.script-option-tile__label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.3;
}

.script-option-tile__hint {
  display: block;
  font-size: 11.5px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.48);
  line-height: 1.35;
  margin-top: 3px;
}

.script-option-tile__switch {
  flex-shrink: 0;
  margin: 0 !important;
  padding: 0 !important;
}

.script-option-tile__switch >>> .v-input--selection-controls__input {
  margin: 0;
}

.script-upload-row-col {
  margin-bottom: 0;
}

.script-upload-row {
  margin: 0;
}

.script-upload-slot-col {
  display: flex;
  flex-direction: column;
}

.script-upload-inline {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 76px;
  padding: 4px 0;
}

.script-upload-inline >>> .script-upload-zone {
  margin: 0;
  height: 100%;
  display: flex;
  align-items: center;
  border-width: 1px;
  padding: 0 10px;
}

.script-upload-inline >>> .v-file-input {
  margin-top: 0;
  padding-top: 0;
}

.script-option-tile--upload {
  height: 100%;
  min-height: 76px;
  box-sizing: border-box;
}

.script-option-tile--priority {
  cursor: default;
  min-height: 76px;
}

.script-option-tile--priority-full {
  width: 100%;
}

.script-option-tile--priority:hover {
  transform: none;
}

.script-option-tile__text--priority {
  flex: 0 1 220px;
  max-width: 280px;
}

.script-priority-radios {
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
  margin-left: 4px;
}

.script-priority-radio {
  flex: 1 1 0;
  min-width: 0;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid rgba(102, 63, 92, 0.18);
  border-radius: 10px;
  background: #f5f2f4;
  color: rgba(0, 0, 0, 0.72);
  font-weight: 600;
  font-size: 13px;
  line-height: 1.2;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
}

.script-priority-radio:hover:not(:disabled):not(.script-priority-radio--active) {
  border-color: rgba(102, 63, 92, 0.35);
  background: #faf7f9;
}

.script-priority-radio--active {
  background: linear-gradient(135deg, #7a4d6c 0%, #663f5c 100%);
  border-color: #663f5c;
  color: #fff;
  box-shadow: 0 2px 8px rgba(102, 63, 92, 0.22);
}

.script-priority-radio:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (max-width: 959px) {
  .script-option-tile--priority-full {
    flex-wrap: wrap;
    align-items: flex-start;
    min-height: auto;
    padding-bottom: 14px;
  }

  .script-option-tile__text--priority {
    flex: 1 1 calc(100% - 56px);
    max-width: none;
  }

  .script-priority-radios {
    flex: 1 1 100%;
    margin-left: 52px;
    margin-top: 4px;
  }
}
</style>
